import { type BoneMetadata } from './BoneAutoMapper'

/**
 * QuaterniusMapper — direct bone-name mapping for the "universal humanoid" naming
 * convention shared by Quaternius's Universal Animation Library, Tripo3D's auto-rig
 * output, Unity Humanoid avatars, and most generic glTF humanoid exports.
 *
 * The convention: Mixamo-compatible bone names without the `mixamorig` prefix —
 * `Hips`, `Spine`, `Spine1`, `LeftShoulder`, `LeftArm`, `LeftForeArm`, `LeftHand`,
 * `LeftUpLeg`, `LeftLeg`, `LeftFoot`, etc.
 *
 * Source: Mesh2Motion skeleton (e.g. `pelvis`, `spine_01`, `clavicle_l`)
 * Target: any rig using the universal-humanoid convention
 *
 * Used in two flows:
 * - Source-side ([`ForeignSkeletonAdapter`](../../features/anim-library/ForeignSkeletonAdapter.ts)):
 *   when a user imports a Quaternius/Tripo animation file, this map renames the foreign
 *   tracks to Mesh2Motion bone names so they bind to the M2M skeleton.
 * - Target-side ([`BoneAutoMapper`](./BoneAutoMapper.ts)): when a user loads a
 *   Tripo-rigged model into the retarget page, `map_quaternius_bones` produces the
 *   target→source mapping so M2M animations retarget onto it without manual drag-drop.
 *
 * Names are based on the v2.0 (2026-01) Quaternius universal-humanoid spec and Tripo3D's
 * "standard humanoid" claim. Verify against the actual file you import — the convention
 * is "mostly stable" in practice, not perfectly stable. The mapper passes through
 * unmapped bones, so partial coverage is harmless.
 */
export class QuaterniusMapper {
  private static readonly BONE_MAP: Record<string, string> = {
    // Torso
    pelvis: 'Hips',
    spine_01: 'Spine',
    spine_02: 'Spine1',
    spine_03: 'Spine2',
    neck_01: 'Neck',
    head: 'Head',
    head_leaf: 'HeadTop_End',

    // Left Arm
    clavicle_l: 'LeftShoulder',
    upperarm_l: 'LeftArm',
    lowerarm_l: 'LeftForeArm',
    hand_l: 'LeftHand',

    // Right Arm
    clavicle_r: 'RightShoulder',
    upperarm_r: 'RightArm',
    lowerarm_r: 'RightForeArm',
    hand_r: 'RightHand',

    // Left Leg
    thigh_l: 'LeftUpLeg',
    calf_l: 'LeftLeg',
    foot_l: 'LeftFoot',
    ball_l: 'LeftToeBase',
    ball_leaf_l: 'LeftToe_End',

    // Right Leg
    thigh_r: 'RightUpLeg',
    calf_r: 'RightLeg',
    foot_r: 'RightFoot',
    ball_r: 'RightToeBase',
    ball_leaf_r: 'RightToe_End',

    // Fingers — included for parity with Mesh2Motion's fully-jointed hand. The Quaternius
    // Universal Animation Library does not animate per-finger bones, so most clips will simply
    // have no track for these and they'll fall through unchanged. If you import a pack that
    // does have finger tracks (e.g. CMU + extension), these names should still apply.
    thumb_01_l: 'LeftHandThumb1',
    thumb_02_l: 'LeftHandThumb2',
    thumb_03_l: 'LeftHandThumb3',
    thumb_04_leaf_l: 'LeftHandThumb4',

    index_01_l: 'LeftHandIndex1',
    index_02_l: 'LeftHandIndex2',
    index_03_l: 'LeftHandIndex3',
    index_04_leaf_l: 'LeftHandIndex4',

    middle_01_l: 'LeftHandMiddle1',
    middle_02_l: 'LeftHandMiddle2',
    middle_03_l: 'LeftHandMiddle3',
    middle_04_leaf_l: 'LeftHandMiddle4',

    ring_01_l: 'LeftHandRing1',
    ring_02_l: 'LeftHandRing2',
    ring_03_l: 'LeftHandRing3',
    ring_04_leaf_l: 'LeftHandRing4',

    pinky_01_l: 'LeftHandPinky1',
    pinky_02_l: 'LeftHandPinky2',
    pinky_03_l: 'LeftHandPinky3',
    pinky_04_leaf_l: 'LeftHandPinky4',

    thumb_01_r: 'RightHandThumb1',
    thumb_02_r: 'RightHandThumb2',
    thumb_03_r: 'RightHandThumb3',
    thumb_04_leaf_r: 'RightHandThumb4',

    index_01_r: 'RightHandIndex1',
    index_02_r: 'RightHandIndex2',
    index_03_r: 'RightHandIndex3',
    index_04_leaf_r: 'RightHandIndex4',

    middle_01_r: 'RightHandMiddle1',
    middle_02_r: 'RightHandMiddle2',
    middle_03_r: 'RightHandMiddle3',
    middle_04_leaf_r: 'RightHandMiddle4',

    ring_01_r: 'RightHandRing1',
    ring_02_r: 'RightHandRing2',
    ring_03_r: 'RightHandRing3',
    ring_04_leaf_r: 'RightHandRing4',

    pinky_01_r: 'RightHandPinky1',
    pinky_02_r: 'RightHandPinky2',
    pinky_03_r: 'RightHandPinky3',
    pinky_04_leaf_r: 'RightHandPinky4'
  }

  /**
   * Heuristic: a skeleton or set of track names is "Quaternius-shaped" if it contains
   * the universal-humanoid sentinels (Hips/Spine/LeftArm) without the `mixamorig` prefix.
   */
  static is_target_valid_skeleton (bone_names: string[]): boolean {
    const lower = bone_names.map(n => n.toLowerCase())
    const has_mixamo_prefix = lower.some(n => n.includes('mixamorig'))
    if (has_mixamo_prefix) return false

    const sentinels = ['hips', 'spine', 'leftarm', 'rightarm']
    const matches = sentinels.filter(s => lower.includes(s)).length
    return matches >= 3
  }

  /** Mesh2Motion → Quaternius name. Returns undefined if the bone has no Quaternius equivalent. */
  static map_source_bone_name_to_quaternius (source_bone_name: string): string | undefined {
    return this.BONE_MAP[source_bone_name]
  }

  /** Quaternius → Mesh2Motion name. Built lazily by inverting BONE_MAP on first call. */
  private static _quaternius_to_m2m: Record<string, string> | null = null
  static map_quaternius_bone_name_to_source (quaternius_name: string): string | undefined {
    if (this._quaternius_to_m2m === null) {
      this._quaternius_to_m2m = {}
      for (const [m2m, q] of Object.entries(this.BONE_MAP)) {
        this._quaternius_to_m2m[q] = m2m
      }
    }
    return this._quaternius_to_m2m[quaternius_name]
  }

  /** The full Quaternius → Mesh2Motion mapping, useful for the runtime track renamer. */
  static get_source_to_target_map (): Record<string, string> {
    const map: Record<string, string> = {}
    for (const [m2m, q] of Object.entries(this.BONE_MAP)) {
      map[q] = m2m
    }
    return map
  }

  /**
   * Map Mesh2Motion bones to Quaternius bones (parallel to MixamoMapper.map_mixamo_bones).
   * @returns Map of target bone name -> source bone name
   */
  static map_quaternius_bones (source_bones: BoneMetadata[], target_bones: BoneMetadata[]): Map<string, string> {
    const mappings = new Map<string, string>()
    for (const source_bone of source_bones) {
      const expected_target_name = this.BONE_MAP[source_bone.name]
      if (expected_target_name === undefined) continue
      const target_bone = target_bones.find(tb => tb.name === expected_target_name)
      if (target_bone !== undefined) {
        mappings.set(target_bone.name, source_bone.name)
      }
    }
    console.log(`Quaternius mapping complete: ${mappings.size} bones mapped`)
    return mappings
  }
}
